<template>
  <div
    data-testid="upload-bar"
    class="flex h-[50px] w-[307px] items-center rounded-full bg-gray-800 py-2 text-[15px] text-white z-100 cursor-pointer tablet-h:w-[528px] tablet-h:cursor-default tablet-h:rounded-[15px] tablet-h:px-[14px] tablet-h:py-2.5"
    :class="barPadding"
    @click="handleBarClick"
  >
    <CircularProgressIndicator
      v-if="uploadStore.uploadsInProgress.length"
      :percentage="avgPercentage"
      stroke-color="#34B1C7"
      text-color="white"
      class="mr-2.5 tablet-h:mr-3"
    />
    <div
      v-else-if="settledIcon"
      class="flex size-[30px] items-center justify-center mr-2.5 tablet-h:mr-3"
    >
      <component
        :is="settledIcon.component"
        class="size-[22px]"
        :class="settledIcon.colorClass"
      />
    </div>
    <div
      v-truncate
      class="flex grow flex-col tablet-h:flex-row tablet-h:items-center"
      :key="copy"
    >
      <div
        data-truncate-target
        class="flex grow items-center mr-2 tablet-h:grow-0"
      >
        {{ copy }}
      </div>
      <div
        v-if="uploadStore.uploadsInProgress.length"
        class="text-white/50 leading-[20px]"
      >
        {{ timeRemaining }}
      </div>
    </div>
    <button
      type="button"
      class="ml-auto hidden cursor-pointer text-[#b8e7ea] tablet-h:block"
      @click.stop="handleViewDetailsClick"
    >
      View details
    </button>
    <button
      v-if="showCloseButton"
      type="button"
      class="ml-2.5 hidden size-[22px] shrink-0 cursor-pointer items-center justify-center tablet-h:flex"
      @click.stop="handleCloseClick"
    >
      <CloseIcon class="size-[12px] text-gray-300" />
    </button>
    <ChevronRightIcon class="ml-2 size-4 shrink-0 text-white tablet-h:hidden" />
  </div>
</template>

<script setup lang="ts">
import { computed, watch, type Component } from 'vue'
import CircularProgressIndicator from '@/shared/components/CircularProgressIndicator.vue'
import { UploadEvent, useUploadStore } from '@/features/upload/store'
import { formatTimeLeft } from '@/shared/utils/datetime'
import {
  CheckmarkIcon,
  AlertIcon,
  CloseIcon,
  ChevronRightIcon,
} from '@dacr/icons'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { useDevice } from '@/shared/composables/useDevice'

const uploadStore = useUploadStore()
const { isMobile } = useDevice()

const hasAnyNonCanceledErrors = computed(() =>
  uploadStore.currentSessionUploads.filter(
    (upload) => upload.error && !upload.canceled
  )
)

watch(uploadStore, () => {
  if (
    uploadStore.finalUploadEvent !== UploadEvent.None &&
    !hasAnyNonCanceledErrors.value.length
  ) {
    setTimeout(() => {
      uploadStore.showUploadBar =
        uploadStore.finalUploadEvent === UploadEvent.None
    }, 7000)
  }
})

const settledIcon = computed<{
  component: Component
  colorClass: string
} | null>(() => {
  if (uploadStore.finalUploadEvent === UploadEvent.Uploaded) {
    return { component: CheckmarkIcon, colorClass: 'text-white' }
  }
  if (
    uploadStore.finalUploadEvent === UploadEvent.Error ||
    uploadStore.finalUploadEvent === UploadEvent.Canceled
  ) {
    return { component: AlertIcon, colorClass: 'text-red-400' }
  }
  return null
})

const barPadding = computed(() => {
  if (uploadStore.uploadsInProgress.length) return 'pl-2.5 pr-[14px]'
  if (settledIcon.value) return 'px-[14px]'
  return 'pl-[18px] pr-[14px]'
})

const copy = computed(() => {
  if (uploadStore.finalUploadEvent === UploadEvent.Uploaded) {
    const uploadedFiles = uploadStore.currentSessionUploads.filter(
      (upload) => upload.percentage === 100 && !upload.error && !upload.canceled
    )
    if (uploadedFiles.length > 1) {
      return `Uploaded ${uploadedFiles.length} files`
    }
    if (uploadedFiles[0]) {
      return `Uploaded ${uploadedFiles[0].fileName}`
    }
    return 'Upload complete'
  }

  if (uploadStore.finalUploadEvent === UploadEvent.Canceled) {
    const canceledFiles = uploadStore.currentSessionUploads.filter(
      (upload) => upload.canceled
    )
    if (canceledFiles.length > 1) {
      return `${canceledFiles.length} uploads canceled`
    }
    return 'Upload cancelled'
  }

  if (uploadStore.finalUploadEvent === UploadEvent.Error) {
    const erroredUploads = uploadStore.currentSessionUploads.filter(
      (upload) => upload.error && !upload.canceled
    )
    if (erroredUploads.length > 1) {
      return `Unable to upload ${erroredUploads.length} files`
    }
    if (erroredUploads[0]) {
      return `Unable to upload ${erroredUploads[0].fileName}`
    }
    return 'Upload failed'
  }

  if (uploadStore.uploadsInProgress.length) {
    if (uploadStore.uploadsInProgress.length > 1) {
      return `Uploading ${uploadStore.uploadsInProgress.length} files`
    }
    return `Uploading ${uploadStore.uploadsInProgress[0].fileName}`
  }

  const processingUploads = uploadStore.currentSessionUploads.filter(
    (upload) => upload.percentage === 0 && !upload.error && !upload.canceled
  )
  if (processingUploads.length > 1) {
    return `Processing ${processingUploads.length} files`
  }
  if (processingUploads[0]) {
    return `Processing ${processingUploads[0].fileName}`
  }
  return ''
})

const avgPercentage = computed(() => {
  const totalPercentage = uploadStore.uploadsInProgress.reduce(
    (acc, upload) => (acc += upload.percentage),
    0
  )
  return Math.round(totalPercentage / uploadStore.uploadsInProgress.length)
})

const maxTimeRemaining = computed(() =>
  Math.max(...uploadStore.uploadsInProgress.map((upload) => upload.timeLeft))
)

const timeRemaining = computed(() => formatTimeLeft(maxTimeRemaining.value))

const showCloseButton = computed(() => {
  return hasAnyNonCanceledErrors.value.length
})

const handleBarClick = () => {
  if (isMobile.value) handleViewDetailsClick()
}

const handleViewDetailsClick = () => {
  openDialog(Dialog.UploadDetails)
}

const handleCloseClick = () => {
  uploadStore.showUploadBar = false
}
</script>
